import lodash from 'lodash';
import {
    QueryNodeVisitor,
    QueryNodeWalker,
    ValueSets,
    ValueSetRegistry,
    Predicates
} from '@complion/query';
import {
    compareValueSets
} from './ValueSetComparer';
import uuid from 'node-uuid'; // TODO: Replace node-uuid with uuid
import { getRawValue } from 'common/util/dataTypeValues';
const { ScalarValue } = ValueSets;

export class PredicateGroupingVisitor extends QueryNodeVisitor {
    constructor(rootNode) {
        super();
        this._predicateOrganizer = new PredicateOrganizer();
        this.rootNode = rootNode;
    }

    get predicateGroups() {
        return this._predicateOrganizer.predicateGroups;
    }

    visitPredicate(context) {
        if (context.currentNode.type === Predicates.DocumentType.name) {
            return;
        }

        const queryNode = lodash.get(this.rootNode, context.path);
        this._predicateOrganizer.addNode(queryNode);
    }

    visitDescriptorPredicate(context) {
        const queryNode = lodash.get(this.rootNode, context.path);
        this._predicateOrganizer.addDescriptorNode(queryNode);
    }
}

export class PredicateOrganizer {
    constructor() {
        this.predicateGroups = new Map();
    }

    _isEqualType(typeOne, typeTwo) {
        return lodash.eq(typeOne, typeTwo);
    }

    _isEqualOperator(operatorOne, operatorTwo) {
        return lodash.eq(operatorOne, operatorTwo);
    }

    _isEqualValue(valueOne, valueTwo) {
        return compareValueSets(valueOne, valueTwo);
    }

    _isEqualDataType(dataTypeOne, dataTypeTwo) {
        dataTypeOne = lodash.isString(dataTypeOne) && dataTypeOne.toLowerCase();
        dataTypeTwo = lodash.isString(dataTypeTwo) && dataTypeTwo.toLowerCase();
        return dataTypeOne === dataTypeTwo;
    }

    _isMatchingKey(predicateGroupKey, queryNode) {
        const value = ValueSetRegistry.reconstituteDeserializedJson(queryNode.value);
        this._correctDataType(value, queryNode);
        return this._isEqualType(predicateGroupKey.type, queryNode.type)
            && this._isEqualOperator(predicateGroupKey.operator, queryNode.operator)
            && this._isEqualValue(predicateGroupKey.value, queryNode.value)
            && this._isEqualDataType(predicateGroupKey.dataType, queryNode.getDataType());
    }

    _createGroupId() {
        return uuid.v4();
    }

    _getNodeGroupKey(queryNode) {
        const keys = Array.from(this.predicateGroups.keys());
        const predicateGroupKey = lodash.find(keys,
            (key) => this._isMatchingKey(key, queryNode)
        );
        const value = ValueSetRegistry.reconstituteDeserializedJson(queryNode.value);
        this._correctDataType(value, queryNode);
        return predicateGroupKey
            ? predicateGroupKey
            : {
                groupId: this._createGroupId(),
                type: queryNode.type,
                operator: queryNode.operator,
                value: ValueSetRegistry.reconstituteDeserializedJson(queryNode.value),
                dataType: queryNode.getDataType(),
                hash: `${queryNode.type}/${queryNode.operator}/${value.type}/${queryNode.getDataType()}`
            };
    }

    _getDescriptorNodeGroupKey(queryNode) {
        const keys = Array.from(this.predicateGroups.keys());
        const predicateGroupKey = lodash.find(keys,
            (key) => this._isMatchingKey(key, queryNode) &&
                key.descriptorId === queryNode.descriptor.id
        );
        const value = ValueSetRegistry.reconstituteDeserializedJson(queryNode.value);
        this._correctDataType(value, queryNode);
        return predicateGroupKey
            ? predicateGroupKey
            : {
                groupId: this._createGroupId(),
                type: queryNode.type,
                operator: queryNode.operator,
                value: ValueSetRegistry.reconstituteDeserializedJson(queryNode.value),
                descriptorId: queryNode.descriptor.id,
                dataType: queryNode.getDataType(),
                hash: `${queryNode.type}/${queryNode.operator}/${value.type}/${queryNode.getDataType()}/${queryNode.descriptor.id}`,
            };
    }

    _correctDataType(value, queryNode) {
        // valueset data type is not being written correctly
        // this is a correction for that
        if (ValueSetRegistry.resolve(value.type).hasDataType) {
            value.dataType = queryNode.getDataType().toLowerCase();
        }
    }

    _isScalarDateTypeDescriptor(predicateGroupKey, queryNode) {
        return predicateGroupKey
            && predicateGroupKey.value._type === ScalarValue.name
            && ['DATEONLY', 'DATETIME'].includes(predicateGroupKey.dataType)
            && queryNode.descriptor.id === predicateGroupKey.descriptorId;
    }

    _isEqualDateTypeDescriptorValue(predicateGroupKey, queryNode) {
        const value = ValueSetRegistry.reconstituteDeserializedJson(queryNode.value);
        const rawValueOne = getRawValue(predicateGroupKey.dataType, value._data.value);
        const rawValueTwo = getRawValue(predicateGroupKey.dataType, predicateGroupKey.value._data.value);

        return predicateGroupKey.value._data.value && rawValueOne.isSame(rawValueTwo);
    }

    addDescriptorNode(queryNode) {
        const predicateGroupKey = this._getDescriptorNodeGroupKey(queryNode);
        let predicateGroup = this.predicateGroups.get(predicateGroupKey);

        if (this._isScalarDateTypeDescriptor(predicateGroupKey, queryNode)
            && !this._isEqualDateTypeDescriptorValue(predicateGroupKey, queryNode)
        ) {
            // Unset the descriptor value for scalar date type descriptors with different values
            predicateGroupKey.value._data.value = null;
            queryNode.value._data.value = null;
        }

        if (lodash.isNil(predicateGroup)) {
            predicateGroup = {
                key: predicateGroupKey,
                queryNodes: [],
                saveValuePropertyPath: 'data.value._data'
            };
            this.predicateGroups.set(predicateGroupKey, predicateGroup);
        }

        predicateGroup.queryNodes.push(queryNode);
    }

    addNode(queryNode) {
        const predicateGroupKey = this._getNodeGroupKey(queryNode);
        let predicateGroup = this.predicateGroups.get(predicateGroupKey);

        if (lodash.isNil(predicateGroup)) {
            predicateGroup = {
                key: predicateGroupKey,
                queryNodes: [],
                saveValuePropertyPath: 'data.value._data'
            };
            this.predicateGroups.set(predicateGroupKey, predicateGroup);
        }

        predicateGroup.queryNodes.push(queryNode);
    }
}

export async function getPredicateGroups(rootNode) {
    const walker = new QueryNodeWalker();
    const visitor = new PredicateGroupingVisitor(rootNode);
    await walker.walk(rootNode, visitor);
    return visitor.predicateGroups;
}